import type { WalkthroughGroup } from '@mesa/core/pull';
import type { ReviewCommentRecord } from '@mesa/core/review';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ChevronDownIcon, ChevronRightIcon, FileIcon } from 'lucide-react';
import { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { MesaCodeReviewBox } from '@/components/mesa-diff/code-review-box';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useFileMutations, viewedFilesQuery } from '@/features/pulls/services/files';
import { parseGitDiff } from '@/lib/diff-parser';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';

declare global {
  interface Window {
    scrollToHeading?: (heading: string) => void;
    scrollToFile?: (path: string) => void;
    openFile?: (path: string) => void;
  }
}

type PullContentProps = {
  pullNodeId: string;
  pullHeadSha: string;
  sortByRelevancy: boolean;
  isGenerating: boolean;
  walkthrough: WalkthroughGroup[];
  diff?: string;
};

const VIEW_MODE = 'split';

const PullContentInner = memo(
  ({ pullNodeId, pullHeadSha, sortByRelevancy, isGenerating, walkthrough, diff }: PullContentProps) => {
    const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

    const { data: viewedFiles, isLoading: isLoadingViewedFiles } = useQuery(viewedFilesQuery(owner, repo, number));

    const z = useZero();

    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    const reviewCommentsQuery = useMemo(() => {
      return z.query.ReviewCommentsTable
        .where('owner', owner)
        .where('repo', repo)
        .where('pullNumber', number)
        .where('isBad', false);
    }, [owner, repo, number]);

    const [reviewComments] = useZeroQuery(reviewCommentsQuery);

    const reviewCommentsForFile = useCallback(
      (path: string): ReviewCommentRecord[] => {
        if (!reviewComments) return [];
        return reviewComments.filter((comment) => comment.path === path);
      },
      [reviewComments]
    );

    // Parse files from diff
    const parsedFiles = useMemo(() => {
      // Always parse the raw diff with our new parser
      if (diff) {
        return parseGitDiff(diff);
      }
      return [];
    }, [diff]);

    const { markFileAsViewed, unmarkFileAsViewed } = useFileMutations(owner, repo, number, pullNodeId);

    const [openFiles, setOpenFiles] = useState<string[]>([]);

    // Get the files to display based on mode
    const filesToDisplay = useMemo(() => {
      if (sortByRelevancy && !isGenerating && walkthrough.length > 0) {
        // In walkthrough mode, find the matching parsed files for each walkthrough group
        return walkthrough.map((group) => {
          // For each file in the walkthrough group, find the matching parsed file
          return {
            ...group,
            files: group.files.map((walkFile) => {
              const filePath = walkFile.type === 'delete' ? walkFile.oldPath : walkFile.newPath;
              // Find matching parsed file
              const matchingParsedFile = parsedFiles.find((parsedFile) => {
                const parsedPath = parsedFile.type === 'delete' ? parsedFile.oldPath : parsedFile.newPath;
                return parsedPath === filePath;
              });
              // Return the parsed file if found, otherwise fall back to the walkthrough file
              return matchingParsedFile || walkFile;
            }),
          };
        });
      } else {
        // In A-Z mode, just use the parsed files
        return [{ heading: 'Changes', description: '', files: parsedFiles }];
      }
    }, [sortByRelevancy, isGenerating, walkthrough, parsedFiles]);

    const isViewed = (path: string) => {
      return isLoadingViewedFiles ? false : viewedFiles?.find((f) => f.path === path)?.viewerViewedState === 'VIEWED';
    };

    const handleViewedChange = (path: string, viewed: boolean) => {
      const file = isLoadingViewedFiles ? null : viewedFiles!.find((f) => f.path === path);
      if (!file) return;

      if (viewed) {
        markFileAsViewed({ path: file.path });
        setOpenFiles((prev) => prev.filter((p) => p !== path));
      } else {
        unmarkFileAsViewed({ path: file.path });
      }
    };

    const isOpen = (path: string) => {
      return openFiles.includes(path);
    };

    const handleOpenChange = (path: string, open: boolean) => {
      if (open) {
        setOpenFiles((prev) => [...prev, path]);
      } else {
        setOpenFiles((prev) => prev.filter((p) => p !== path));
      }
    };

    const toggleGroupCollapse = (groupId: string) => {
      setCollapsedGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }
        return newSet;
      });
    };

    // Destructure toggleGroupCollapse for dependency array
    const toggleGroupCollapseFunc = toggleGroupCollapse;

    useEffect(() => {
      // Set up scrolling to headings for walkthrough mode
      window.scrollToHeading = (heading: string) => {
        if (!sortByRelevancy) return;

        const groupIndex = filesToDisplay.findIndex((g) => g.heading === heading);
        if (groupIndex === -1) return;
        console.log('scroll group', groupIndex);

        const item = document.querySelector(`#walkthrough-group-${groupIndex}`);
        console.log('scroll item', item);
        if (!item) return;
        item.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      // Set up scrolling to files
      window.scrollToFile = (path: string) => {
        // Find which group contains the file
        const groupIndex = filesToDisplay.findIndex(
          (g) => g.files.findIndex((f) => (f.type === 'delete' ? f.oldPath === path : f.newPath === path)) !== -1
        );

        if (groupIndex === -1) return;

        // Find the file index within the group
        const fileIndex = filesToDisplay[groupIndex].files.findIndex((f) =>
          f.type === 'delete' ? f.oldPath === path : f.newPath === path
        );

        if (fileIndex === -1) return;

        // Find the DOM element and scroll to it
        const elementId = sortByRelevancy
          ? `walkthrough-file-${groupIndex}-${fileIndex}`
          : `walkthrough-file-${fileIndex}`;

        const item = document.querySelector(`#${elementId}`);
        if (!item) return;

        // Ensure the group is expanded
        if (collapsedGroups.has(filesToDisplay[groupIndex].heading)) {
          toggleGroupCollapseFunc(filesToDisplay[groupIndex].heading);
        }

        // Scroll to the item
        item.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      // Set up opening files
      window.openFile = (path: string) => {
        // Find which group contains the file
        const groupIndex = filesToDisplay.findIndex(
          (g) => g.files.findIndex((f) => (f.type === 'delete' ? f.oldPath === path : f.newPath === path)) !== -1
        );

        if (groupIndex === -1) return;

        // Find the file in the group
        const file = filesToDisplay[groupIndex].files.find((f) =>
          f.type === 'delete' ? f.oldPath === path : f.newPath === path
        );

        if (!file) return;

        // Use the newPath for opening (this is what the handleOpenChange expects)
        const filePath = file.type === 'delete' ? file.oldPath : file.newPath;

        // Ensure the group is expanded
        if (collapsedGroups.has(filesToDisplay[groupIndex].heading)) {
          toggleGroupCollapseFunc(filesToDisplay[groupIndex].heading);
        }

        // Open the file if it's not already open
        if (!isOpen(filePath)) {
          handleOpenChange(filePath, true);
        }
      };

      return () => {
        window.scrollToHeading = undefined;
        window.scrollToFile = undefined;
        window.openFile = undefined;
      };
    }, [filesToDisplay, sortByRelevancy, collapsedGroups, toggleGroupCollapseFunc, isOpen, handleOpenChange]);

    // Map of groupId -> heading height (measured once on mount/when refs change)
    const [headingHeights, setHeadingHeights] = useState<Record<string, number>>({});

    useEffect(() => {
      const heights: Record<string, number> = {};
      filesToDisplay.forEach((group, idx) => {
        const el = document.getElementById(`walkthrough-heading-${idx}`);
        if (el) {
          heights[group.heading] = el.getBoundingClientRect().height;
        }
      });
      setHeadingHeights(heights);
    }, [filesToDisplay]);

    if (!isGenerating && !filesToDisplay.length) {
      return <div className="p-4 text-center text-muted-foreground">No changes found in this pull request.</div>;
    }

    if (isGenerating) {
      return (
        <div className="space-y-4">
          <Skeleton className="w-full rounded-md h-32" />
          <Skeleton className="w-full rounded-md h-32" />
          <Skeleton className="w-full rounded-md h-32" />
          <Skeleton className="w-full rounded-md h-32" />
        </div>
      );
    }

    return (
      <div className="w-full h-full space-y-4 px-4 pb-6">
        {filesToDisplay.map((group, index) => {
          const isGroupCollapsed = collapsedGroups.has(group.heading);
          return (
            <div
              key={group.heading + index}
              data-index={index}
              className={cn({ 'pt-6': index > 0 })}
              id={`walkthrough-group-${index}`}
            >
              {/* Only show heading and description in walkthrough mode or if it's not the default "Changes" heading */}
              {(sortByRelevancy || group.heading !== 'Changes') && (
                <>
                  {/* Heading */}
                  <h3
                    className={cn(
                      'text-xl font-semibold break-words text-wrap overflow-hidden select-text',
                      // Stick the heading under the app header while its section is in view
                      'sticky top-0 z-20 backdrop-blur-sm bg-stone-50 py-2'
                    )}
                    id={`walkthrough-heading-${index}`}
                  >
                    {group.heading}
                  </h3>

                  {/* Description - indented but no vertical line */}
                  {group.description && (
                    <div className={cn('break-words text-wrap overflow-hidden select-text', 'pl-3')}>
                      <MarkdownEditor value={group.description} editable={false} owner={owner} />
                    </div>
                  )}

                  {/* File group control with vertical line */}
                  <div className="relative pl-1">
                    <div
                      className="flex items-center gap-2 text-sm py-2 cursor-pointer hover:text-blue-500"
                      onClick={() => toggleGroupCollapse(group.heading)}
                    >
                      <div className="flex items-center">
                        {isGroupCollapsed ? <ChevronRightIcon size={16} /> : <ChevronDownIcon size={16} />}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FileIcon className="size-3.5" />
                        <span className="text-sm">
                          {group.files.length} file{group.files.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {!isGroupCollapsed && (
                      <div className="relative">
                        {/* Single vertical line for all files */}
                        <div className="absolute left-[7px] w-px bg-gray-200 h-full" />

                        {/* Files list */}
                        <div className="pl-4 space-y-4 pt-1">
                          {group.files.map((file, fileIndex) => (
                            <div
                              key={file.newPath + fileIndex}
                              id={`walkthrough-file-${index}-${fileIndex}`}
                              className="relative"
                            >
                              <MesaCodeReviewBox
                                filePath={file.type === 'delete' ? file.oldPath : file.newPath}
                                hunks={file.hunks}
                                viewed={file.type === 'delete' ? isViewed(file.oldPath) : isViewed(file.newPath)}
                                viewMode={VIEW_MODE}
                                className="transition-all"
                                open={isOpen(file.newPath)}
                                onOpenChange={(open) => handleOpenChange(file.newPath, open)}
                                onViewedChange={(viewed) => {
                                  const path = file.type === 'delete' ? file.oldPath : file.newPath;
                                  handleViewedChange(path, viewed);
                                }}
                                reviewComments={
                                  file.type === 'delete'
                                    ? reviewCommentsForFile(file.oldPath)
                                    : reviewCommentsForFile(file.newPath)
                                }
                                owner={owner}
                                repo={repo}
                                sha={pullHeadSha}
                                stickyOffset={headingHeights[group.heading] ?? 0}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* When not in walkthrough mode or for the default "Changes" heading, just show files directly */}
              {(!sortByRelevancy || group.heading === 'Changes') && !isGroupCollapsed && !group.description && (
                <div className="space-y-4">
                  {group.files.map((file, fileIndex) => (
                    <div
                      key={file.newPath + fileIndex}
                      id={`walkthrough-file-${fileIndex}`}
                      className={cn('relative pl-0')}
                    >
                      <MesaCodeReviewBox
                        filePath={file.type === 'delete' ? file.oldPath : file.newPath}
                        hunks={file.hunks}
                        viewed={file.type === 'delete' ? isViewed(file.oldPath) : isViewed(file.newPath)}
                        viewMode={VIEW_MODE}
                        className="transition-all"
                        open={isOpen(file.newPath)}
                        onOpenChange={(open) => handleOpenChange(file.newPath, open)}
                        onViewedChange={(viewed) => {
                          const path = file.type === 'delete' ? file.oldPath : file.newPath;
                          handleViewedChange(path, viewed);
                        }}
                        reviewComments={
                          file.type === 'delete'
                            ? reviewCommentsForFile(file.oldPath)
                            : reviewCommentsForFile(file.newPath)
                        }
                        owner={owner}
                        repo={repo}
                        sha={pullHeadSha}
                        stickyOffset={0}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

export const PullWalkthrough = memo(
  ({ walkthrough, sortByRelevancy = true, diff = '', isGenerating, pullNodeId, pullHeadSha }: PullContentProps) => {
    return (
      <div className="flex flex-col w-full" data-section-name={sortByRelevancy ? 'Walkthrough' : 'Changes'}>
        <div className="pt-4 pb-4 px-4">
          <Separator className="w-full" />
        </div>
        <Suspense
          fallback={
            <div className="space-y-4">
              <Skeleton className="w-full rounded-md h-32" />
              <Skeleton className="w-full rounded-md h-32" />
              <Skeleton className="w-full rounded-md h-32" />
              <Skeleton className="w-full rounded-md h-32" />
            </div>
          }
        >
          <PullContentInner
            sortByRelevancy={sortByRelevancy}
            isGenerating={isGenerating}
            pullNodeId={pullNodeId}
            pullHeadSha={pullHeadSha}
            walkthrough={walkthrough}
            diff={diff}
          />
        </Suspense>
      </div>
    );
  }
);
